<script setup lang="ts">
    import { ChevronDownIcon } from '@lucide/vue';
    import { ref, watch } from 'vue';
    import { useAppStore, ILanguage } from '../stores/app';
    import { usePermissions } from '@/js/composables/usePermissions';
    import { AskMeModulePermissionFlag, PermissionArea } from '../consts/permissions';

    type SuggestedQuestion = {
        id: string;
        title: string;
        prompt: string;
    };

    type SuggestedQuestionCategory = {
        id: string;
        title: string;
        questions: SuggestedQuestion[];
    };

    const emit = defineEmits<{
        (e: 'select', prompt: string): void;
    }>();

    const languageImports: Record<ILanguage, () => Promise<SuggestedQuestionCategory[]>> = {
        sv: () =>
            import('../consts/suggestedQuestions/suggested-questions-sv').then(
                (m) => m.suggestedQuestionsSv
            ),
        no: () =>
            import('../consts/suggestedQuestions/suggested-questions-no').then(
                (m) => m.suggestedQuestionsNo
            ),
        en: () =>
            import('../consts/suggestedQuestions/suggested-questions-en').then(
                (m) => m.suggestedQuestionsEn
            ),
        da: () =>
            import('../consts/suggestedQuestions/suggested-questions-da').then(
                (m) => m.suggestedQuestionsDa
            ),
        fi: () =>
            import('../consts/suggestedQuestions/suggested-questions-fi').then(
                (m) => m.suggestedQuestionsFi
            ),
    };

    const appStore = useAppStore();

    const suggestedQuestions = ref<SuggestedQuestionCategory[]>([]);

    const { hasPermission } = usePermissions();

    const loadSuggestedQuestions = async (language: ILanguage) => {
        const loader = languageImports[language] ?? languageImports.sv;
        suggestedQuestions.value = await loader();
    };

    watch(
        () => appStore.language,
        async (language) => {
            await loadSuggestedQuestions(language);
        },
        { immediate: true }
    );

    const send = (prompt: string) => {
        if (!hasAskMeAccess.value) {
            return;
        }
        emit('select', prompt);
    };

    const hasAskMeAccess = hasPermission(
        PermissionArea.AskMeModule,
        AskMeModulePermissionFlag.Access
    );
</script>

<template>
    <div class="flex flex-col gap-3">
        <div
            v-for="category in suggestedQuestions"
            :key="category.id"
            class="has-open:shadow-md rounded has-open:mb-8 relative z-20"
        >
            <details class="Accordion rounded overflow-hidden text-sm">
                <summary
                    class="appearance-none custom-focus cursor-pointer relative bg-primary-plum border-2 ring-transparent rounded-t-md focus:-border-offset-3 not-in-open:rounded-b-md focus:ring-3 text-white px-4 py-2.5 pr-8"
                >
                    {{ category.title }}
                    <ChevronDownIcon class="size-5 -my-0.5 -mx-2 absolute top-3 right-4" />
                </summary>

                <div>
                    <ul class="max-h-64 overflow-y-scroll custom-scrollbar">
                        <li
                            v-for="question in category.questions"
                            :key="question.id"
                            class="mx-px border-b last:border-b-0 border-b-gray-lightest"
                        >
                            <button
                                class="text-left px-4 py-2.5 block w-full hover:bg-gray-lightest cursor-pointer custom-focus"
                                @click="send(question.prompt)"
                            >
                                {{ question.title }}
                            </button>
                        </li>
                    </ul>
                </div>
            </details>
        </div>
    </div>
</template>
